<template>
  <section
    ref="siteHeader"
    class="site-header-group"
    :class="$class(headerState)"
  >
    <slot />
  </section>
</template>

<script>

/**
 * Vue: Site header group (site-header-group)
 * -----------------------------------------------------------------------------
 * Site header section group component.
 * - Used to set header height and control state.
 * - See site header for main visible component.
 *
 */
import { mapActions, mapGetters, mapState } from 'vuex'

export default {
  name: 'SiteHeaderGroup',

  data() {
    return {
      height: {
        total: null,
        collapsed: null,
        minimised: null,
        promoBanner: null,
        promoBannerVisible: null,
        open: null,
        visible: null,
      },
      sections: [],
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      headerState: (state) => state.index.scroll.header,
      isMobile: (state) => state.index.screen.mobile,
      loadedStylesheets: (state) => state.index.stylesheets,
    }),
  },

  watch: {

    /**
     * Watch header state to update height variables.
     * - Wait for site header transition to end before updating height.
     * - Set height immediately if prefers reduced motion.
     */
    headerState() {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.setHeights()
        return
      }

      this.$refs.siteHeader.ontransitionend = (args) => {
        if (!args || !['transform', 'translate'].includes(args.propertyName)) {
          return
        }

        this.setHeights()
      }
    },

    /**
     * Watch isMobile to re-calculate header heights.
     */
    isMobile: {
      async handler() {
        await this.$nextTick()
        this.setHeights(true)
      },
      immediate: true,
    },

    /**
     * Watch loaded stylesheets to set height.
     */
    async loadedStylesheets() {
      if (!this.getLoadedStylesheet('layout.theme.min.css')) {
        return
      }

      /**
       * Wait for CSS to be parsed.
       */
      await cnvs.Sleep(200)
      this.setHeights(true)
    },
  },

  async mounted() {
    await this.$nextTick()
    this.setHeights(true)
    this.setEventListeners()
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      setScrollStart: 'setScrollStart',
    }),

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getLoadedStylesheet: 'getLoadedStylesheet',
    }),

    /**
     * Set heights of fixed header.
     * - Height.total is used for body offset.
     * - Height.visible is used for overlays etc.
     * - State heights are used for translate offset.
     * - Remove setHeights if header is not fixed.
     * @param {Boolean} mounted - If height is being set on mounted.
     */
    setHeights(mounted) {
      if (mounted) {
        this.getHeaderSections()
        this.height.total = this.getHeaderStateHeight('open')
        this.height.collectionStickyHeader = this.getCollectionStickyHeaderHeight()

        this.height.open = this.height.total - this.height.collectionStickyHeader
        this.height.collapsed = this.getHeaderStateHeight('collapsed')
        this.height.minimised = this.getHeaderStateHeight('minimised')
      }

      /**
       * Visible height based on current state.
       */
      this.height.promoBanner = this.getPromoBannerHeight()
      this.height.promoBannerVisible = this.getPromoBannerHeight()
      this.height.visible = this.height[this.headerState]

      this.height.promoBannerVisible = this.height.total === this.height.visible
        ? this.height.promoBanner
        : 0

      /**
       * Update scroll start height.
       * - This is the point at which the site header will collapse.
       */
      this.setScrollStart(this.height.total)

      /**
       * Update CSS variables.
       */
      if (this.height.collectionStickyHeader > 0 && this.isMobile) {
        this.height.minimised = this.height.collapsed - this.height.total
        this.height.total = this.height.collapsed
      }

      this.updateCssVariables()
    },

    /**
     * Get header sections and states.
     * - .shopify-section-group-site-header class is added by Shopify.
     * - Select first <section> element inside it.
     * - Determine states from data-site-header-states attribute.
     */
    getHeaderSections() {
      const sections = [...document.querySelectorAll('#vue-element .shopify-section-group-site-header-group > section')]

      this.sections = sections.map((element) => {
        if (!element.dataset.siteHeaderStates) {
          window.console.warn('An element in the site header group does not have the header states snippet attributes')
        }

        return {
          element,
          handle: element.dataset?.handle
            ? element.dataset?.handle
            : element.dataset?.componentHandle,
          states: {
            collapsed: element.dataset.siteHeaderStates?.includes('collapsed'),
            minimised: element.dataset.siteHeaderStates?.includes('minimised'),
            open: element.dataset.siteHeaderStates?.includes('open'),
          },
        }
      })
    },

    /**
     * Get header state height.
     * @param {String} state - State to get height for.
     * @returns {Number}
     */
    getHeaderStateHeight(state) {
      let height = 0
      if (state === 'collapsed') {
        this.sections.forEach((section) => {
          if (section.states?.[state]) {
            return
          }
          height += section.element.offsetHeight
        })
      } else {
        this.sections.forEach((section) => {
          if (!section.states?.[state]) {
            return
          }

          height += section.element.offsetHeight
        })
      }

      return height
    },

    /**
     * Get promo banner height.
     * @returns Number
     */
    getPromoBannerHeight() {
      const promoBanner = this.sections.find((section) => {
        return section.handle === 'promo-banner'
      })

      return promoBanner.element.offsetHeight
    },

    /**
     * Get collection sticky header height.
     * @returns Number
     */
    getCollectionStickyHeaderHeight() {
      const collectionStickyHeader = this.sections.find((section) => {
        return section.handle === 'collection-sticky-header'
      })

      return collectionStickyHeader?.element?.offsetHeight || 0
    },

    /**
     * Update CSS variables.
     */
    updateCssVariables() {
      document.documentElement.style.setProperty(
        '--header-height',
        `${this.height.total}px`,
      )

      document.documentElement.style.setProperty(
        '--header-visible-height',
        `${this.height.visible}px`,
      )

      document.documentElement.style.setProperty(
        '--header-collapsed-translate',
        `-${this.height.total}px`,
      )

      document.documentElement.style.setProperty(
        '--header-minimised-translate',
        `-${this.height.total - this.height.minimised}px`,
      )

      document.documentElement.style.setProperty(
        '--promo-banner-height',
        `${this.height.promoBannerVisible}px`,
      )

      document.documentElement.style.setProperty(
        '--promo-banner-height-static',
        `${this.height.promoBanner}px`,
      )

      document.documentElement.style.setProperty(
        '--header-collection-sticky',
        `${this.height.collectionStickyHeader}px`,
      )

      document.documentElement.style.setProperty(
        '--header-open',
        `${this.height.open}px`,
      )
    },

    /**
     * Set event listeners.
     * - Listens for site header async component event to update heights.
     */
    setEventListeners() {
      cnvs.EventBus.listen('cnvs:update-site-header-height', () => {
        this.setHeights(true)
      })
    },
  },
}
</script>
